import {
  ArrowRightLeft,
  Bot,
  Bell,
  BellRing,
  BookOpen,
  Boxes,
  CalendarClock,
  Database,
  FileText,
  GitBranch,
  Globe,
  Home,
  Inbox,
  Link2,
  KeyRound,
  Layers3,
  LayoutGrid,
  ListOrdered,
  RotateCcw,
  Settings2,
  Shield,
  Siren,
  SlidersHorizontal,
  Users,
  Webhook,
  Workflow,
  Rocket,
  PackageCheck,
  Waypoints
} from 'lucide-react';
import type { PageDefinition, ProjectSection } from '../types/navigation';

export const workspaceProductSections: ProjectSection[] = [
  {
    label: 'Work',
    items: [
      {
        id: 'work-inbox',
        group: 'Work',
        label: 'Inbox',
        subtitle: 'Work requiring intervention',
        description:
          'Fix failed delivery work, approve gates, and assign anything without an owner.',
        icon: Inbox
      },
      {
        id: 'pipeline-runs',
        group: 'Work',
        label: 'Delivery map',
        subtitle: 'Every change from task to production',
        description:
          'Follow work from task to production and find the stage where it stopped.',
        icon: Workflow
      }
    ]
  },
  {
    label: 'Agents',
    items: [
      {
        id: 'agent-work',
        group: 'Agents',
        label: 'Agent work',
        subtitle: 'Human-led plans from real tasks',
        description:
          'Choose work from ClickUp or Plane, pick a configured role, and review a practical plan before anything is acted on.',
        icon: Bot
      },
      {
        id: 'agent-runners',
        group: 'Agents',
        label: 'Runners',
        subtitle: 'Runner machines and access',
        description:
          'Register customer-managed machines, manage their access, and check their connection to Cindro.',
        icon: Workflow
      }
    ]
  },
  {
    label: 'Delivery',
    items: [
      {
        id: 'shipments',
        group: 'Delivery',
        label: 'Release control',
        subtitle: 'Coordinate releases across projects',
        description:
          'Plan and follow API, web, and app releases across this workspace.',
        icon: PackageCheck
      }
    ]
  },
  {
    label: 'Configure',
    items: [
      {
        id: 'connections',
        group: 'Configure',
        label: 'Connections',
        subtitle: 'Task trackers and repositories',
        description:
          'Connect ClickUp or Plane and link the GitHub repositories Cindro should follow.',
        icon: Link2
      },
      {
        id: 'delivery-model',
        group: 'Configure',
        label: 'Delivery setup',
        subtitle: 'Pipeline tracks and stages',
        description:
          'Set the stages each change moves through, from its source task to a live release.',
        icon: Waypoints
      }
    ]
  },
  {
    label: 'Workspace',
    items: [
      {
        id: 'guide',
        group: 'Workspace',
        label: 'Guide',
        subtitle: 'Set up and operate Cindro',
        description:
          'Set up repositories, release paths, targets and integrations.',
        icon: BookOpen
      },
      {
        id: 'applications',
        group: 'Workspace',
        label: 'Projects',
        subtitle: 'Project catalog',
        description:
          'Track projects, the apps they contain, and which capabilities each project enables.',
        icon: LayoutGrid
      }
    ]
  }
];

export const workspacePageSections: ProjectSection[] = [
  ...workspaceProductSections,
  {
    label: 'Admin',
    items: [
      {
        id: 'settings',
        group: 'Admin',
        label: 'Settings',
        subtitle: 'Configuration',
        description: 'Manage the workspace, ownership and lifecycle settings.',
        icon: Settings2
      },
      {
        id: 'audit-log',
        group: 'Admin',
        label: 'Audit log',
        subtitle: 'History',
        description:
          'Track every workspace action, deployment event, and access change.',
        icon: FileText
      },
      {
        id: 'policies',
        group: 'Admin',
        label: 'Policies',
        subtitle: 'Governance',
        description: 'Set the rules that can block or approve a release.',
        icon: Shield
      }
    ]
  },
  {
    label: 'Access',
    items: [
      {
        id: 'members',
        group: 'Access',
        label: 'Members',
        subtitle: 'Team access',
        description:
          'Manage organization members, roles, and invite new collaborators.',
        icon: Users
      },
      {
        id: 'release-access',
        group: 'Access',
        label: 'API keys',
        subtitle: 'Cindro API keys',
        description: 'Create and rotate Cindro API keys for operator access.',
        icon: KeyRound
      },
      {
        id: 'storage-policy',
        group: 'Access',
        label: 'Storage',
        subtitle: 'Bundle hosting',
        description:
          'Manage the global bundle storage policy and hosting backend.',
        icon: Database
      }
    ]
  }
];

export const workspaceOperationalPageIds = [
  'work-inbox',
  'pipeline-runs',
  'agent-work',
  'shipments',
  'agent-runners',
  'connections',
  'delivery-model',
  'guide',
  'applications'
] as const;

export const workspaceAdminPageIds = [
  'members',
  'release-access',
  'audit-log',
  'settings',
  'storage-policy',
  'policies'
] as const;

// Route scope follows ownership in the navigation model. This prevents a new
// workspace page from silently being routed as if it belonged to a project.
export const workspaceScopedPageIds = workspacePageSections.flatMap((section) =>
  section.items.map((item) => item.id)
);

export function isWorkspaceScopedPage(pageId: string) {
  return (workspaceScopedPageIds as readonly string[]).includes(pageId);
}

export function isWorkspaceAdminPage(pageId: string) {
  return (workspaceAdminPageIds as readonly string[]).includes(pageId);
}

export const projectPageSections: ProjectSection[] = [
  {
    label: 'Project',
    items: [
      {
        id: 'overview',
        group: 'Project',
        label: 'Overview',
        subtitle: 'Status and next actions',
        description: 'See what is live, what is blocked and where to go next.',
        icon: Home
      }
    ]
  },
  {
    label: 'Release',
    items: [
      {
        id: 'promotions',
        group: 'Release',
        label: 'Deploy update',
        subtitle: 'Ship a bundle',
        description:
          'Choose a bundle and the exact app version and environment that should receive it.',
        icon: ArrowRightLeft
      },
      {
        id: 'releases',
        group: 'Release',
        label: 'Bundles',
        subtitle: 'Builds ready to ship',
        description:
          'Find uploaded builds, see which are live, and choose one to deploy.',
        icon: Boxes
      },
      {
        id: 'channels',
        group: 'Release',
        label: 'What is live',
        subtitle: 'Versions by environment',
        description:
          'See the bundle currently served to each app version and environment.',
        icon: GitBranch
      },
      {
        id: 'rollback',
        group: 'Release',
        label: 'Recovery',
        subtitle: 'Restore a previous bundle',
        description:
          'Restore one app version and environment to a previously deployed bundle.',
        icon: RotateCcw
      }
    ]
  },
  {
    label: 'Configuration',
    items: [
      {
        id: 'runtime-config',
        group: 'Configuration',
        label: 'Runtime rules',
        subtitle: 'API, CDN and matching',
        description:
          'Control the API, CDN, analytics, and update target returned to each app.',
        icon: SlidersHorizontal
      },
      {
        id: 'app-releases',
        group: 'Configuration',
        label: 'App versions',
        subtitle: 'iOS and Android versions',
        description:
          'Register the iOS and Android versions that can receive an update.',
        icon: Rocket
      },
      {
        id: 'environments',
        group: 'Configuration',
        label: 'Environments',
        subtitle: 'Development to production',
        description:
          'Choose which environments appear in day-to-day release screens.',
        icon: Layers3
      },
      {
        id: 'domains',
        group: 'Configuration',
        label: 'Domains',
        subtitle: 'Deep links & CORS',
        description:
          'Claim and verify domains, and set the native app identity (bundle ID, Team ID, cert fingerprints) each one deep-links into.',
        icon: Link2
      }
    ]
  },
  {
    label: 'Monitoring',
    items: [
      {
        id: 'alert-sources',
        group: 'Monitoring',
        label: 'Alert sources',
        subtitle: 'Inbound webhooks',
        description:
          'Receive Grafana and other alerts through signed webhook endpoints.',
        icon: Webhook
      }
    ]
  },
  {
    label: 'On-Call',
    items: [
      {
        id: 'escalation-runs',
        group: 'On-Call',
        label: 'Escalation runs',
        subtitle: 'Active and past alerts',
        description:
          'Every alert that entered an escalation policy, most recent first, with the ability to acknowledge open runs.',
        icon: Siren
      },
      {
        id: 'oncall-schedules',
        group: 'On-Call',
        label: 'Schedules',
        subtitle: 'Rotations and shifts',
        description:
          'Define on-call rotation schedules for a team and assign concrete shifts.',
        icon: CalendarClock
      },
      {
        id: 'escalation-policies',
        group: 'On-Call',
        label: 'Escalation policies',
        subtitle: 'Notification steps',
        description:
          'Define who gets notified, on which channel, and in what order when an alert fires.',
        icon: ListOrdered
      }
    ]
  },
  {
    label: 'Operations',
    items: [
      {
        id: 'diagnostics',
        group: 'Operations',
        label: 'Health checks',
        subtitle: 'Connectivity and setup',
        description:
          'Check whether the project is connected and ready to deliver updates.',
        icon: Bell
      }
    ]
  },
  {
    label: 'Notifications',
    items: [
      {
        id: 'notifications-overview',
        group: 'Notifications',
        label: 'Overview',
        subtitle: 'Delivery plan',
        description:
          'Plan delivery infrastructure for the future messaging product.',
        icon: BellRing
      },
      {
        id: 'notifications-providers',
        group: 'Notifications',
        label: 'Providers',
        subtitle: 'Lanes and vendors',
        description: 'Define providers, credentials, and delivery fallbacks.',
        icon: Globe
      },
      {
        id: 'notifications-templates',
        group: 'Notifications',
        label: 'Templates',
        subtitle: 'Message content',
        description:
          'Manage notification templates, variables, and brand variants.',
        icon: FileText
      },
      {
        id: 'notifications-workflows',
        group: 'Notifications',
        label: 'Workflows',
        subtitle: 'Event routing',
        description: 'Build event-to-message routing and multi-step journeys.',
        icon: Workflow
      },
      {
        id: 'notifications-delivery',
        group: 'Notifications',
        label: 'Delivery log',
        subtitle: 'Sent events',
        description:
          'Audit notification delivery, retries, and channel outcomes.',
        icon: Bell
      }
    ]
  }
];

const pageSections = [...workspacePageSections, ...projectPageSections];

export const pageLookup: Record<string, PageDefinition> = Object.fromEntries(
  pageSections.flatMap((section) =>
    section.items.map((item) => [item.id, item])
  )
) as Record<string, PageDefinition>;

export const legacyPageIdAliases: Record<string, string> = {
  'ota-apps': 'applications',
  'ota-app-releases': 'app-releases',
  'ota-releases': 'releases',
  'ota-channels': 'channels',
  'ota-promotions': 'promotions',
  'ota-rollback': 'rollback',
  'ota-runtime': 'runtime-config',
  'ota-storage': 'storage-policy',
  'ota-access': 'release-access',
  'ota-diagnostics': 'diagnostics'
};

export const defaultEnvironmentNames = [
  'dev',
  'staging',
  'production',
  'internal'
] as const;

export const CHECKLIST_DISMISSED_KEY = 'cindro_setup_checklist_dismissed';
